import type { ReceiptPaperSize, ReceiptPrinterConfig } from './thermalReceiptTypes';
import {
  DEFAULT_RECEIPT_PAPER_SIZE,
  isReceiptPaperSize,
} from './thermalReceiptTypes';

/** Browser / Electron renderer localStorage key for kiosk receipt printer binding. */
export const RECEIPT_PRINTER_STORAGE_KEY = 'incashflow.receiptPrinterConfig';

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function resolveStorage(backend?: KeyValueStorage | null): KeyValueStorage | null {
  if (backend !== undefined) return backend;
  if (typeof globalThis.localStorage === 'undefined') return null;
  return globalThis.localStorage;
}

function normalizePaperSize(value: unknown): ReceiptPaperSize {
  return isReceiptPaperSize(value) ? value : DEFAULT_RECEIPT_PAPER_SIZE;
}

function isValidConfig(value: unknown): value is ReceiptPrinterConfig {
  if (!value || typeof value !== 'object') return false;
  const config = value as ReceiptPrinterConfig;
  return typeof config.printerName === 'string' && config.printerName.trim().length > 0;
}

export function readReceiptPrinterConfigStorage(
  backend?: KeyValueStorage | null,
): ReceiptPrinterConfig | null {
  const storage = resolveStorage(backend);
  if (!storage) return null;

  const raw = storage.getItem(RECEIPT_PRINTER_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as ReceiptPrinterConfig;
    if (!isValidConfig(parsed)) return null;
    return {
      printerName: parsed.printerName.trim(),
      configuredAt: parsed.configuredAt ?? new Date().toISOString(),
      configuredBy: parsed.configuredBy ?? '',
      paperSize: normalizePaperSize(parsed.paperSize),
    };
  } catch {
    return null;
  }
}

export function writeReceiptPrinterConfigStorage(
  config: ReceiptPrinterConfig,
  backend?: KeyValueStorage | null,
): void {
  const storage = resolveStorage(backend);
  if (!storage) {
    throw new Error('LOCAL_STORAGE_UNAVAILABLE');
  }

  const normalized: ReceiptPrinterConfig = {
    printerName: config.printerName.trim(),
    configuredAt: config.configuredAt ?? new Date().toISOString(),
    configuredBy: config.configuredBy ?? '',
    paperSize: normalizePaperSize(config.paperSize),
  };

  storage.setItem(RECEIPT_PRINTER_STORAGE_KEY, JSON.stringify(normalized));
}

export function clearReceiptPrinterConfigStorage(
  backend?: KeyValueStorage | null,
): void {
  const storage = resolveStorage(backend);
  storage?.removeItem(RECEIPT_PRINTER_STORAGE_KEY);
}
